import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { AtlassianLogo } from '@atlaskit/logo';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useSearchParams } from 'react-router-dom';
import errorImage from '../images/Error.svg';
import { useAnalyticsAnonymousId } from '../utilities/analytics/analytics-web-client';
import { isFedM } from '../utilities/env';
import FedrampUseIdProbe from '../utilities/debug/FedrampUseIdProbe';

interface Props {
  title: ReactNode;
  children?: ReactNode;
}

type BaseErrorPageProps = Props & {
  anonymousId?: string;
  errorCode?: string | null;
  errorDescription?: string | null;
};

const styles = cssMap({
  diagnosticDetail: {
    display: 'block',
    font: token('font.body.small'),
    overflowWrap: 'anywhere',
  },
});

export default function ErrorPage({ title, children }: Props) {
  const anonymousId = useAnalyticsAnonymousId();
  const [searchParams] = useSearchParams();

  return (
    <BaseErrorPage
      title={title}
      anonymousId={anonymousId}
      errorCode={searchParams.get('error')}
      errorDescription={searchParams.get('error_description')}>
      {children}
      {isFedM() && <FedrampUseIdProbe context="error-page" />}
    </BaseErrorPage>
  );
}

export function BaseErrorPage({
  title,
  anonymousId,
  errorCode,
  errorDescription,
  children,
}: BaseErrorPageProps) {
  return (
    <ErrorContainer>
      <AtlassianLogo size="large" label="Atlassian" appearance="brand" />
      <div>
        <img src={errorImage} height="150" alt="" className="error-image" />
      </div>
      <h2 className="error-title">{title}</h2>
      {!!children && <div className="error-content">{children}</div>}

      <small className="error-diagnostic-details">
        {errorCode && (
          <Box as="span" xcss={styles.diagnosticDetail}>
            error: {errorCode}
          </Box>
        )}
        {errorDescription && (
          <Box as="span" xcss={styles.diagnosticDetail}>
            error_description: {errorDescription}
          </Box>
        )}
        <code>{anonymousId || '—'}</code>
      </small>
    </ErrorContainer>
  );
}

const ErrorContainer = styled.div`
  margin-right: auto;
  margin-left: auto;
  max-width: 900px;
  padding: 100px 10px 0px 10px;
  text-align: center;

  .error-image {
    padding: 24px 0px;
  }

  .error-title {
    padding-top: 0px;
  }

  .error-content {
    padding-top: 16px;
  }

  .error-action {
    padding-top: 24px;
  }

  .error-diagnostic-details {
    display: block;
    padding-top: 24px;
  }
`;
